/**
 * NotificationItem
 * Individual notification item in the dropdown
 */

import { useNavigate } from 'react-router-dom';
import { X, Check } from 'lucide-react';
import {
  Notification,
  NOTIFICATION_PRIORITY_COLORS,
  NOTIFICATION_PRIORITY_LABELS
} from '@/types/notification';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { parseDbTimestamp } from '@/utils/datetime';
import { cn } from '@/utils/cn';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
}

export function NotificationItem({ notification, onMarkAsRead, onDelete }: NotificationItemProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }

    if (notification.actionUrl) {
      // Only navigate to internal paths — reject external URLs and protocol-relative URLs
      const url = notification.actionUrl;
      if (url.startsWith('/') && !url.startsWith('//')) {
        navigate(url);
      }
    }
  };

  const handleMarkAsRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    onMarkAsRead(notification.id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete(notification.id);
  };

  const createdAt = parseDbTimestamp(notification.createdAt);
  const timeAgo = createdAt
    ? formatDistanceToNow(createdAt, { addSuffix: true, locale: ptBR })
    : '-';

  return (
    <div
      className={cn(
        'cursor-pointer border-b border-border px-4 py-3 transition-colors',
        notification.read ? 'bg-card hover:bg-muted' : 'bg-accent/60 hover:bg-accent'
      )}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            {!notification.read && (
              <span className="size-2 shrink-0 rounded-full bg-primary" aria-hidden="true" />
            )}
            <h4 className={cn('truncate text-sm text-foreground', notification.read ? 'font-medium' : 'font-semibold')}>
              {notification.title}
            </h4>
          </div>

          {notification.message && (
            <p className="mb-2 line-clamp-2 break-words text-sm text-muted-foreground">
              {notification.message}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span
              className={NOTIFICATION_PRIORITY_COLORS[notification.priority]}
              title={`Prioridade ${NOTIFICATION_PRIORITY_LABELS[notification.priority]}`}
              aria-hidden="true"
            >
              ●
            </span>
            <span>{timeAgo}</span>
            {notification.actionLabel && (
              <>
                <span aria-hidden="true">•</span>
                <span className="text-primary">{notification.actionLabel}</span>
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {!notification.read && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={handleMarkAsRead}
                  aria-label="Marcar como lida"
                  className="hover:text-primary"
                >
                  <Check />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Marcar como lida</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                onClick={handleDelete}
                aria-label="Excluir notificação"
                className="hover:text-destructive"
              >
                <X />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Excluir</TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}
